// Impersonation slice — tracks whether the current Redux session is
// the result of an admin "Prise de contrôle". The localStorage is the
// source of truth across hard reloads; this reducer is hydrated from
// `gd_impersonation` on init and the start/stop action thunks mutate
// both localStorage AND Redux atomically.
//
// We deliberately keep the slice tiny — just enough metadata to render
// the banner and let the user exit. Everything else (current user,
// access token, etc.) is handled by the existing auth flow which is
// already swapped to the target user during impersonation.

import { Dispatch } from 'redux';
import api from './api';
import { discardActing } from '@/app/lib/networkActing';

// ---------- localStorage keys ------------------------------------------------
// Reading them in one place so the migration to cookie-backed storage later
// (if we want HttpOnly for the access token) is a single-file change.
export const IMPERSONATION_STATE_KEY = 'gd_impersonation';
export const ORIGINAL_USER_KEY = 'gd_impersonation_original_user';
export const ORIGINAL_TOKEN_KEY = 'gd_impersonation_original_token';
export const ORIGINAL_REFRESH_KEY = 'gd_impersonation_original_refresh';

// ---------- action types -----------------------------------------------------
const IMPERSONATION_HYDRATE = '[IMPERSONATION] HYDRATE';
const IMPERSONATION_STARTED = '[IMPERSONATION] STARTED';
const IMPERSONATION_STOPPED = '[IMPERSONATION] STOPPED';

// ---------- types ------------------------------------------------------------
export interface ImpersonationMeta {
  impersonationUuid: string;
  // The admin who initiated the takeover — used in the banner so the
  // text says "Back to <Sophie>" rather than just "Back".
  actorUuid: string;
  actorFirstName: string | null;
  actorLastName: string | null;
  actorEmail: string | null;
  // The user being impersonated — shown in the banner ("Connected as ...").
  targetUuid: string;
  targetFirstName: string | null;
  targetLastName: string | null;
  targetEmail: string | null;
  startedAt: string;
}

interface ImpersonationState {
  active: ImpersonationMeta | null;
  loading: boolean;
  error: string | null;
}

interface HydrateAction { type: typeof IMPERSONATION_HYDRATE; payload: ImpersonationMeta | null }
interface StartedAction { type: typeof IMPERSONATION_STARTED; payload: ImpersonationMeta }
interface StoppedAction { type: typeof IMPERSONATION_STOPPED }
type ImpersonationAction = HydrateAction | StartedAction | StoppedAction;

// ---------- helpers ----------------------------------------------------------
function readActiveFromStorage(): ImpersonationMeta | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(IMPERSONATION_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.impersonationUuid) return null;
    return parsed as ImpersonationMeta;
  } catch {
    return null;
  }
}

function writeActiveToStorage(meta: ImpersonationMeta | null) {
  if (typeof window === 'undefined') return;
  try {
    if (meta) {
      window.localStorage.setItem(IMPERSONATION_STATE_KEY, JSON.stringify(meta));
    } else {
      window.localStorage.removeItem(IMPERSONATION_STATE_KEY);
    }
  } catch {
    /* quota / disabled storage — non-fatal */
  }
}

// ---------- action creators --------------------------------------------------
export const hydrateImpersonation = (): HydrateAction => ({
  type: IMPERSONATION_HYDRATE,
  payload: readActiveFromStorage(),
});

// Start an impersonation. The caller is responsible for triggering a
// hard reload after dispatch so the rest of the app picks up the new
// JWT (cart, booking, axios interceptors all re-init from scratch).
export const startImpersonation = (targetUuid: string, reason?: string) =>
  async (dispatch: Dispatch<ImpersonationAction>) => {
    // Snapshot the current user + tokens BEFORE we swap them. These
    // keys are what `stopImpersonation` reads to restore the admin's
    // session — never overwritten while impersonating.
    const localStorageOrEmpty = (k: string) => {
      try { return window.localStorage.getItem(k); } catch { return null; }
    };

    const actorUserRaw = localStorageOrEmpty('user');
    const actorAccessToken = localStorageOrEmpty('access_token');
    const actorRefreshToken = localStorageOrEmpty('refresh_token');
    let actorUser: any = null;
    try { actorUser = actorUserRaw ? JSON.parse(actorUserRaw) : null; } catch {}
    const actorPayload =
      (actorUser && (actorUser.user || actorUser.data)) || actorUser || null;

    // POST /api/admin/impersonate { targetUuid }
    const res = await api.post('/api/admin/impersonate', {
      targetUuid,
      reason: reason || null,
    });
    const data = res.data || {};
    const newAccessToken: string | null = data.access_token || null;
    // Dedicated impersonation refresh token (carries impersonatedBy + the
    // audit-log uuid). Swapping it into the live `refresh_token` slot is
    // what makes the takeover survive the 2h access-token expiry WITHOUT
    // the axios interceptor reaching for the admin's refresh token and
    // silently dropping the session back to the admin.
    const newRefreshToken: string | null = data.refresh_token || null;
    const targetUser = data.user || null;
    const impersonationUuid: string = data.impersonationUuid;

    if (!newAccessToken || !targetUser || !impersonationUuid) {
      throw new Error('Impersonation response is missing required fields.');
    }

    // Stash the actor's original credentials so we can restore them
    // when the user clicks "Sortir du compte simulé".
    try {
      if (actorPayload) {
        window.localStorage.setItem(ORIGINAL_USER_KEY, JSON.stringify(actorPayload));
      }
      if (actorAccessToken) {
        window.localStorage.setItem(ORIGINAL_TOKEN_KEY, actorAccessToken);
      }
      if (actorRefreshToken) {
        window.localStorage.setItem(ORIGINAL_REFRESH_KEY, actorRefreshToken);
      }
    } catch {}

    // Swap the live session over to the target user. The shape of the
    // `user` localStorage key mirrors what the login flow writes —
    // an object whose `.user` (or root) carries the identifying fields.
    try {
      window.localStorage.setItem('access_token', newAccessToken);
      // Swap the refresh token over too. The admin's original was already
      // snapshotted into ORIGINAL_REFRESH_KEY above, and stopImpersonation
      // restores it — so while impersonating, the refresh slot belongs to
      // the impersonated session, never the admin.
      if (newRefreshToken) {
        window.localStorage.setItem('refresh_token', newRefreshToken);
      } else {
        // Backend didn't return one (older build): drop the admin's
        // refresh token rather than leave it live, so a 401 can't quietly
        // refresh us back into the admin session.
        window.localStorage.removeItem('refresh_token');
      }
      window.localStorage.setItem(
        'user',
        JSON.stringify({ user: targetUser, access_token: newAccessToken })
      );
    } catch {}

    const meta: ImpersonationMeta = {
      impersonationUuid,
      actorUuid: actorPayload?.uuid || '',
      actorFirstName: actorPayload?.firstName || null,
      actorLastName: actorPayload?.lastName || null,
      actorEmail: actorPayload?.email || null,
      targetUuid: targetUser.uuid,
      targetFirstName: targetUser.firstName || null,
      targetLastName: targetUser.lastName || null,
      targetEmail: targetUser.email || null,
      startedAt: new Date().toISOString(),
    };

    writeActiveToStorage(meta);
    dispatch({ type: IMPERSONATION_STARTED, payload: meta });

    return meta;
  };

// Stop the impersonation, restoring the actor's original credentials.
// Caller should hard-reload right after so axios interceptors re-init.
export const stopImpersonation = () =>
  async (dispatch: Dispatch<ImpersonationAction>) => {
    const active = readActiveFromStorage();

    // Tell the backend the session is over (best-effort: a network
    // failure here is non-fatal because the localStorage swap is what
    // actually ends the impersonation client-side).
    if (active?.impersonationUuid) {
      try {
        await api.post('/api/admin/impersonate/stop', {
          impersonationUuid: active.impersonationUuid,
        });
      } catch {
        /* best-effort */
      }
    }

    // Restore the admin's session.
    try {
      const originalUser = window.localStorage.getItem(ORIGINAL_USER_KEY);
      const originalToken = window.localStorage.getItem(ORIGINAL_TOKEN_KEY);
      const originalRefresh = window.localStorage.getItem(ORIGINAL_REFRESH_KEY);

      if (originalToken) {
        window.localStorage.setItem('access_token', originalToken);
      }
      if (originalRefresh) {
        window.localStorage.setItem('refresh_token', originalRefresh);
      } else {
        // No admin refresh token was snapshotted — make sure we don't
        // leave the (now revoked) impersonation refresh token behind.
        window.localStorage.removeItem('refresh_token');
      }
      if (originalUser) {
        const parsed = JSON.parse(originalUser);
        window.localStorage.setItem(
          'user',
          JSON.stringify({ user: parsed, access_token: originalToken })
        );
      }
    } catch {}

    // Wipe ALL the impersonation crumbs so the next page load is
    // indistinguishable from a normal admin session.
    try {
      window.localStorage.removeItem(ORIGINAL_USER_KEY);
      window.localStorage.removeItem(ORIGINAL_TOKEN_KEY);
      window.localStorage.removeItem(ORIGINAL_REFRESH_KEY);
      window.localStorage.removeItem(IMPERSONATION_STATE_KEY);
    } catch {}

    // La couche RESEAU est jetee avec, jamais restauree.
    //
    // Les deux prises d'identite s'empilent : un administrateur peut simuler un
    // siege qui agit lui-meme pour une de ses agences. En quittant la couche
    // exterieure, celle du dessous appartient a une identite qui n'existe plus
    // dans cette session. Laissee en place, elle affichait une barre « Vous
    // agissez au nom de X » par-dessus une session redevenue administrateur, et
    // son bouton de sortie (`endActing`) reposait le jeton du siege — donc
    // basculait l'administrateur dans un compte qu'il venait de quitter.
    discardActing();

    dispatch({ type: IMPERSONATION_STOPPED });
  };

// ---------- reducer ----------------------------------------------------------
const initialState: ImpersonationState = {
  active: null,
  loading: false,
  error: null,
};

const impersonationReducer = (
  state: ImpersonationState = initialState,
  action: ImpersonationAction
): ImpersonationState => {
  switch (action.type) {
    case IMPERSONATION_HYDRATE:
      return { ...state, active: action.payload };
    case IMPERSONATION_STARTED:
      return { ...state, active: action.payload, loading: false, error: null };
    case IMPERSONATION_STOPPED:
      return { ...state, active: null, loading: false, error: null };
    default:
      return state;
  }
};

export default impersonationReducer;
